import React, { useState } from 'react';
import { BPForm } from '@common/BPForm';
import { AuthField } from './Common/AuthField';
import { AuthCheckbox } from './Common/AuthCheckbox';
import { AuthLayout } from './Common/AuthLayout';
import { Button, Divider, Header, Message, Segment } from 'semantic-ui-react';
import * as yup from 'yup';
import { authClient } from './authClient';
import { Login2faRequest } from '@app/api2/api';
import { AuthLayoutRow } from './Common/AuthLayoutRow';
import { useTranslation2 } from '../Contexts/TranslationContext2';
import { buildUrl, AppRoutes } from '../AppRoutes';
import { BPButton } from '@/components/bp/bpbutton';

interface Login2faFormValues {
    twoFactorCode: string;
    rememberMachine: boolean;
}

export const Login2fa: React.FC = () => {
    const { translations: t } = useTranslation2();

    const [loginError, setLoginError] = useState<string>('');

    const urlParams = new URLSearchParams(window.location.search);
    const returnUrl = urlParams.get('returnUrl') || '/';
    const rememberMe = urlParams.get('rememberMe') === 'true';

    const initialValues: Login2faFormValues = {
        twoFactorCode: '',
        rememberMachine: false
    };

    const validationSchema: yup.SchemaOf<Login2faFormValues> = yup.object().shape({
        twoFactorCode: yup.string()
            .min(6, 'The code must be at least 6 characters long')
            .max(7, 'The code must be at most 7 characters long')
            .required('Authenticator code is required'),
        rememberMachine: yup.boolean().required()
    });

    const handleSubmit = async (values: Login2faFormValues) => {
        setLoginError('');

        try {
            const request: Login2faRequest = {
                twoFactorCode: values.twoFactorCode,
                rememberMachine: values.rememberMachine,
                rememberMe: rememberMe
            };

            const result = await authClient.loginWith2fa(request);

            if (result.success) {
                window.location.assign(returnUrl);
            } else if (result.isLockedOut) {
                window.location.assign(buildUrl(AppRoutes.Lockout, {}));
            } else {
                setLoginError(result.errorMessage || 'Invalid authenticator code.');
            }
        } catch (error) {
            console.error('2FA login error:', error);
            setLoginError('An error occurred during login. Please try again.');
        }
    };

    return (
        <AuthLayout header={t.auth.twoFactorAuthentication} description={t.auth.twoFactorAuthenticatorPrompt}>
            <AuthLayoutRow>
                <Segment>
                    {loginError && (
                        <Message error>
                            {loginError}
                        </Message>
                    )}

                    <BPForm
                        initialValues={initialValues}
                        vaildationSchema={validationSchema}
                        onSubmit={handleSubmit}
                        hideButtons={true}
                    >
                        {(formProps) => (
                            <>
                                <AuthField
                                    name="twoFactorCode"
                                    label={t.auth.authenticatorCode}
                                    autoComplete="off"
                                    placeholder={t.auth.authenticatorCode}
                                />

                                <AuthCheckbox
                                    name="rememberMachine"
                                    label={t.auth.rememberThisMachine}
                                />

                                <Divider />

                                <BPButton
                                    className='bp-primary'
                                    type="submit"
                                    primary
                                    size="large"
                                    fluid
                                    disabled={formProps.isSubmitting}
                                    loading={formProps.isSubmitting}
                                >
                                    {t.account.login}
                                </BPButton>
                            </>
                        )}
                    </BPForm>
                </Segment>
            </AuthLayoutRow>

            <AuthLayoutRow>
                <p style={{ textAlign: 'center', marginTop: '1rem' }}>
                    {t.auth.noAuthenticatorAccessPrompt}{' '}
                    <a href={buildUrl(AppRoutes.LoginRecovery, { returnUrl: returnUrl })}>
                        {t.auth.logInWithRecoveryCode}
                    </a>.
                </p>
            </AuthLayoutRow>


        </AuthLayout>
    );
};